import React from 'react';
import { Select, NumberField, Text, TextArea, type SelectOption } from '@chatsdet/ui';
import { SEED_MAX_WAIT_SECONDS, flowTypeUsesInlineWaits } from '@chatsdet/types';
type WaitUnit = 'minutes' | 'hours' | 'days';
export type WaitMode = 'delay' | 'condition';

type WaitNodeConfigProps = {
  localData: Record<string, any>;
  setLocalData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  /** Owning flow's type — seed/teardown flows run waits inline and are capped. */
  flowType?: string;
};

const UNIT_SECONDS: Record<WaitUnit, number> = {
  minutes: 60,
  hours: 60 * 60,
  days: 24 * 60 * 60,
};

const UNIT_OPTIONS: SelectOption[] = [
  { id: 'minutes', label: 'Minutes' },
  { id: 'hours', label: 'Hours' },
  { id: 'days', label: 'Days' },
];

const SEGMENTED_MAX_SECONDS = 60 * 60 * 24 * 30; // 30 days
const MIN_SECONDS = 60;
const DEFAULT_ESTIMATED_WAIT_SECONDS = 1800;
const DEFAULT_CHECK_INTERVAL_SECONDS = 300;
const DEFAULT_MAX_TIMEOUT_SECONDS = 7200;
const DEFAULT_AGENT_FALLBACK_CHECKS = 3;

const CONDITION_UNIT_OPTIONS: SelectOption[] = [
  { id: 'minutes', label: 'Minutes' },
  { id: 'hours', label: 'Hours' },
  { id: 'days', label: 'Days' },
];

export const SMART_WAIT_DEFAULTS = {
  estimatedWaitSeconds: DEFAULT_ESTIMATED_WAIT_SECONDS,
  checkIntervalSeconds: DEFAULT_CHECK_INTERVAL_SECONDS,
  maxTimeoutSeconds: DEFAULT_MAX_TIMEOUT_SECONDS,
  agentFallbackAfterChecks: DEFAULT_AGENT_FALLBACK_CHECKS,
} as const;

export const clampSmartWaitSeconds = (
  seconds: number,
  fallback: number,
  maxSeconds = SEGMENTED_MAX_SECONDS
) => {
  if (!Number.isFinite(seconds)) return fallback;
  return Math.min(Math.max(Math.floor(seconds), MIN_SECONDS), maxSeconds);
};

export const deriveDurationUnitAndValue = (
  seconds: number,
  maxSeconds = SEGMENTED_MAX_SECONDS
): { unit: WaitUnit; value: number } => {
  const safeSeconds = clampSmartWaitSeconds(seconds, MIN_SECONDS, maxSeconds);
  if (safeSeconds % UNIT_SECONDS.days === 0) {
    return { unit: 'days', value: safeSeconds / UNIT_SECONDS.days };
  }
  if (safeSeconds % UNIT_SECONDS.hours === 0) {
    return { unit: 'hours', value: safeSeconds / UNIT_SECONDS.hours };
  }
  return {
    unit: 'minutes',
    value: Math.max(1, Math.round(safeSeconds / UNIT_SECONDS.minutes)),
  };
};

/** Longest wait this flow type allows. Seed/teardown sleep inline, so they're capped. */
export const maxDelaySecondsFor = (flowType?: string) =>
  flowTypeUsesInlineWaits(flowType) ? SEED_MAX_WAIT_SECONDS : SEGMENTED_MAX_SECONDS;

export const clampDelaySeconds = (seconds: number, flowType?: string) => {
  const max = maxDelaySecondsFor(flowType);
  const fallback = Math.min(1800, max);
  if (!Number.isFinite(seconds)) return fallback;
  return Math.min(Math.max(Math.floor(seconds), MIN_SECONDS), max);
};

const deriveUnitAndValue = (
  delaySeconds: number,
  flowType?: string
): { unit: WaitUnit; value: number } => {
  const seconds = clampDelaySeconds(delaySeconds, flowType);
  // Inline waits top out at 5 minutes, so hours/days are meaningless there.
  if (flowTypeUsesInlineWaits(flowType)) {
    return { unit: 'minutes', value: Math.max(1, Math.round(seconds / UNIT_SECONDS.minutes)) };
  }
  if (seconds % UNIT_SECONDS.days === 0)
    return { unit: 'days', value: seconds / UNIT_SECONDS.days };
  if (seconds % UNIT_SECONDS.hours === 0)
    return { unit: 'hours', value: seconds / UNIT_SECONDS.hours };
  return { unit: 'minutes', value: Math.max(1, Math.round(seconds / UNIT_SECONDS.minutes)) };
};

export const WaitNodeConfig: React.FC<WaitNodeConfigProps> = ({
  localData,
  setLocalData,
  flowType,
}) => {
  const inlineWaits = flowTypeUsesInlineWaits(flowType);
  const configuredMode: WaitMode = localData.mode === 'condition' ? 'condition' : 'delay';
  // Seed/teardown flows run waits inline and therefore cannot use the
  // asynchronous condition variant. Existing invalid data is rendered as a
  // delay so the editor never offers an unsupported mode.
  const mode: WaitMode = inlineWaits ? 'delay' : configuredMode;

  if (mode === 'condition') {
    return (
      <SmartWaitConfig
        localData={localData}
        setLocalData={setLocalData}
        onModeChange={(nextMode) =>
          setLocalData((prev) => ({
            ...prev,
            mode: nextMode,
            ...(nextMode === 'condition' ? SMART_WAIT_DEFAULTS : {}),
          }))
        }
      />
    );
  }

  const defaultSeconds = inlineWaits ? MIN_SECONDS : 1800;
  const delaySecondsRaw =
    typeof localData.delaySeconds === 'number' ? localData.delaySeconds : defaultSeconds;
  const derived = React.useMemo(
    () => deriveUnitAndValue(delaySecondsRaw, flowType),
    [delaySecondsRaw, flowType]
  );
  // Stored value is over this flow type's ceiling (e.g. a node authored at the
  // old 30-minute default before the flow became a seed). The field renders the
  // clamped value; this flags the divergence until the user saves.
  const exceedsCap = delaySecondsRaw > maxDelaySecondsFor(flowType);

  const [unit, setUnit] = React.useState<WaitUnit>(derived.unit);
  const [value, setValue] = React.useState<number>(derived.value);

  React.useEffect(() => {
    setUnit(derived.unit);
    setValue(derived.value);
  }, [derived.unit, derived.value]);

  const persist = React.useCallback(
    (next: { unit: WaitUnit; value: number }) => {
      const seconds = clampDelaySeconds(next.value * UNIT_SECONDS[next.unit], flowType);
      setLocalData((prev) => ({
        ...prev,
        mode: 'delay',
        delaySeconds: seconds,
      }));
    },
    [setLocalData, flowType]
  );

  // Max value in the currently selected unit.
  const maxSeconds = maxDelaySecondsFor(flowType);
  const maxValues: Record<WaitUnit, number> = {
    minutes: Math.floor(maxSeconds / UNIT_SECONDS.minutes),
    hours: Math.max(1, Math.floor(maxSeconds / UNIT_SECONDS.hours)),
    days: Math.max(1, Math.floor(maxSeconds / UNIT_SECONDS.days)),
  };

  return (
    <div className="space-y-4">
      {!inlineWaits && (
        <Select
          label="Wait type"
          items={WAIT_MODE_OPTIONS}
          selectedKey="delay"
          onSelectionChange={(key) => {
            if (String(key) !== 'condition') return;
            setLocalData((prev) => ({
              ...prev,
              mode: 'condition',
              ...SMART_WAIT_DEFAULTS,
            }));
          }}
        />
      )}
      <Text size="sm" className="text-muted-foreground">
        {inlineWaits
          ? `Seed and teardown flows pause in place, so waits are limited to ${
              SEED_MAX_WAIT_SECONDS / 60
            } minutes.`
          : 'Wait nodes pause execution and schedule the next segment to run later.'}
      </Text>

      {exceedsCap && (
        // The field above already shows the clamped value, but nothing is saved
        // until the user hits Save — so without this the stored duration stays
        // over the cap and a later validation error cites a number the panel
        // never displayed.
        <Text size="sm" className="text-warning">
          {`This wait is still saved as ${Math.round(
            delaySecondsRaw / 60
          )} minutes. Save to reduce it to ${SEED_MAX_WAIT_SECONDS / 60} minutes.`}
        </Text>
      )}

      <div className="grid grid-cols-2 gap-3">
        <NumberField
          label="Delay"
          value={value}
          onChange={(v: number | null) => {
            const nextValue = v ?? 1;
            setValue(nextValue);
            persist({ unit, value: nextValue });
          }}
          minValue={1}
          maxValue={maxValues[unit]}
        />

        {inlineWaits ? (
          <div className="flex flex-col justify-end pb-2">
            <Text size="sm" className="text-muted-foreground">
              Minutes
            </Text>
          </div>
        ) : (
          <Select
            label="Unit"
            items={UNIT_OPTIONS}
            selectedKey={unit}
            onSelectionChange={(key) => {
              const nextUnit = String(key) as WaitUnit;
              setUnit(nextUnit);
              persist({ unit: nextUnit, value });
            }}
          />
        )}
      </div>
    </div>
  );
};

const WAIT_MODE_OPTIONS: SelectOption[] = [
  { id: 'delay', label: 'Fixed delay' },
  { id: 'condition', label: 'Until a condition passes' },
];

type SmartWaitConfigProps = {
  localData: Record<string, any>;
  setLocalData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  onModeChange: (mode: WaitMode) => void;
};

const SmartWaitConfig: React.FC<SmartWaitConfigProps> = ({
  localData,
  setLocalData,
  onModeChange,
}) => {
  const estimatedWaitSeconds =
    typeof localData.estimatedWaitSeconds === 'number'
      ? localData.estimatedWaitSeconds
      : SMART_WAIT_DEFAULTS.estimatedWaitSeconds;
  const checkIntervalSeconds =
    typeof localData.checkIntervalSeconds === 'number'
      ? localData.checkIntervalSeconds
      : SMART_WAIT_DEFAULTS.checkIntervalSeconds;
  const maxTimeoutSeconds =
    typeof localData.maxTimeoutSeconds === 'number'
      ? localData.maxTimeoutSeconds
      : SMART_WAIT_DEFAULTS.maxTimeoutSeconds;
  const agentFallbackAfterChecks =
    typeof localData.agentFallbackAfterChecks === 'number'
      ? localData.agentFallbackAfterChecks
      : SMART_WAIT_DEFAULTS.agentFallbackAfterChecks;

  const update = React.useCallback(
    (key: string, value: number | string) => {
      setLocalData((prev) => ({ ...prev, mode: 'condition', [key]: value }));
    },
    [setLocalData]
  );

  return (
    <div className="space-y-4">
      <Select
        label="Wait type"
        items={WAIT_MODE_OPTIONS}
        selectedKey="condition"
        onSelectionChange={(key) => onModeChange(String(key) as WaitMode)}
      />

      <Text size="sm" className="text-muted-foreground">
        Check the page asynchronously until this condition passes. Each check resumes the segment
        briefly, then closes the browser while the workflow waits again.
      </Text>

      <TextArea
        label="Pass condition"
        placeholder="e.g., The confirmation banner says that the order was submitted"
        value={localData.passCondition || ''}
        onChange={(value) => update('passCondition', value)}
        description="Describe the visible page state that must be true before the next segment starts."
        rows={3}
        autoExpand
      />

      <div className="grid gap-3 sm:grid-cols-3">
        <SmartWaitDurationField
          label="Estimated wait"
          seconds={estimatedWaitSeconds}
          onChange={(seconds) => update('estimatedWaitSeconds', seconds)}
          description="First check"
        />
        <SmartWaitDurationField
          label="Check interval"
          seconds={checkIntervalSeconds}
          onChange={(seconds) => update('checkIntervalSeconds', seconds)}
          description="Time between checks"
        />
        <SmartWaitDurationField
          label="Maximum timeout"
          seconds={maxTimeoutSeconds}
          onChange={(seconds) => update('maxTimeoutSeconds', seconds)}
          description="Fail if not ready"
        />
      </div>

      <NumberField
        label="Agent fallback after failed checks"
        value={agentFallbackAfterChecks}
        onChange={(value: number | null) =>
          update('agentFallbackAfterChecks', Math.max(1, Math.floor(value ?? 1)))
        }
        minValue={1}
        description="Earlier checks use the cached assertion; this check and later ones can ask an agent."
      />

      <Text size="xs" className="text-muted-foreground">
        The assertion is cacheable. Confirmed cached failures wait cheaply until the fallback
        threshold; uncertain checks can ask an agent immediately.
      </Text>
    </div>
  );
};

const SmartWaitDurationField: React.FC<{
  label: string;
  seconds: number;
  onChange: (seconds: number) => void;
  description: string;
}> = ({ label, seconds, onChange, description }) => {
  const derived = React.useMemo(() => deriveDurationUnitAndValue(seconds), [seconds]);
  const [unit, setUnit] = React.useState<WaitUnit>(derived.unit);
  const [value, setValue] = React.useState(derived.value);

  React.useEffect(() => {
    setUnit(derived.unit);
    setValue(derived.value);
  }, [derived.unit, derived.value]);

  const persist = (nextUnit: WaitUnit, nextValue: number) => {
    const safeValue = Number.isFinite(nextValue) ? Math.max(1, Math.floor(nextValue)) : 1;
    setUnit(nextUnit);
    setValue(safeValue);
    onChange(clampSmartWaitSeconds(safeValue * UNIT_SECONDS[nextUnit], MIN_SECONDS));
  };

  return (
    <div className="space-y-2">
      <NumberField
        label={label}
        value={value}
        onChange={(nextValue: number | null) => persist(unit, nextValue ?? 1)}
        minValue={1}
        maxValue={Math.floor(SEGMENTED_MAX_SECONDS / UNIT_SECONDS[unit])}
        description={description}
      />
      <Select
        aria-label={`${label} unit`}
        items={CONDITION_UNIT_OPTIONS}
        selectedKey={unit}
        onSelectionChange={(key) => persist(String(key) as WaitUnit, value)}
      />
    </div>
  );
};
